import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, Outlet } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { SiteProvider } from '@/context/SiteContext';
import { ProtectedRoute, RoleRoute, ServiceRoute } from '@/components/ProtectedRoute';
import AppLayout from '@/components/layout/AppLayout';
import { getRolePrefix } from '@/hooks/useRolePath';
import { getRoleBasedRoute } from '@/services/authService';

// Auth Pages (eager — entry points)
import Login from '@/pages/auth/Login';
import Register from '@/pages/auth/Register';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import ResetPassword from '@/pages/auth/ResetPassword';

// --- Lazy-loaded pages (code-split into separate chunks) ---

// Main Pages
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const VisitScreen = lazy(() => import('@/pages/VisitScreen'));
const Patient360 = lazy(() => import('@/pages/Patient360'));
const Patient360Report = lazy(() => import('@/pages/Patient360Report'));
const PatientList = lazy(() => import('@/pages/PatientList'));
const LabManagement = lazy(() => import('@/pages/LabManagement'));
const LaboratorySystem = lazy(() => import('@/pages/LaboratorySystem'));
const PharmacyManagement = lazy(() => import('@/pages/PharmacyManagement'));
const InventoryManagement = lazy(() => import('@/pages/InventoryManagement'));
const InventoryAnalytics = lazy(() => import('@/pages/InventoryAnalytics'));
const BillingManagement = lazy(() => import('@/pages/BillingManagement'));
const ReportingAnalytics = lazy(() => import('@/pages/ReportingAnalytics'));
const AdvancedAnalytics = lazy(() => import('@/pages/AdvancedAnalytics'));
const Reports = lazy(() => import('@/pages/Reports'));
const RadiologyRIS = lazy(() => import('@/pages/RadiologyRIS'));

// IPD & Nursing
const InpatientManagement = lazy(() => import('@/pages/InpatientManagement'));
const BedManagement = lazy(() => import('@/pages/BedManagement'));
const NursingStation = lazy(() => import('@/pages/NursingStation'));

// OT & Procedures
const OTManagement = lazy(() => import('@/pages/OTManagement'));
const SurgeryDetail = lazy(() => import('@/pages/SurgeryDetail'));

// Insurance & Claims (RCM)
const InsuranceManagement = lazy(() => import('@/pages/InsuranceManagement'));
const ClaimsManagement = lazy(() => import('@/pages/ClaimsManagement'));

// Document Management
const DocumentManagement = lazy(() => import('@/pages/DocumentManagement'));

// Patient Portal
const PatientPortal = lazy(() => import('@/pages/PatientPortal'));
const PatientLogin = lazy(() => import('@/pages/patient-portal/PatientLogin'));
const PatientDashboard = lazy(() => import('@/pages/patient-portal/PatientDashboard'));

// Admin
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminConfiguration = lazy(() => import('@/pages/AdminConfiguration'));
const PlatformSettings = lazy(() => import('@/pages/admin/PlatformSettings'));
const DepartmentTemplatesAdmin = lazy(() => import('@/pages/admin/DepartmentTemplatesAdmin'));

// Public
const GetStarted = lazy(() => import('@/pages/public/GetStarted'));

// Super Admin
const SuperAdminDashboard = lazy(() => import('@/pages/superadmin/SuperAdminDashboard'));
const TenantDetail = lazy(() => import('@/pages/superadmin/TenantDetail'));
const PlatformMasterData = lazy(() => import('@/pages/superadmin/PlatformMasterData'));
const SubscriptionManagement = lazy(() => import('@/pages/superadmin/SubscriptionManagement'));
const BillingConsolidation = lazy(() => import('@/pages/superadmin/BillingConsolidation'));

// Tenant Admin
const TenantAdminDashboard = lazy(() => import('@/pages/tenantadmin/TenantAdminDashboard'));
const SiteManagement = lazy(() => import('@/pages/tenantadmin/SiteManagement'));
const UserManagement = lazy(() => import('@/pages/tenantadmin/UserManagement'));
const TenantAnalytics = lazy(() => import('@/pages/tenantadmin/TenantAnalytics'));

// Health Intelligence
const WearableIntegration = lazy(() => import('@/pages/WearableIntegration'));
const RemoteMonitoring = lazy(() => import('@/pages/RemoteMonitoring'));

// Patient Visits & Appointments
const PatientVisitsPage = lazy(() => import('@/pages/PatientVisitsPage'));
const PatientMedicalRecordsPage = lazy(() => import('@/pages/PatientMedicalRecordsPage'));

// Longevity Module
const LongevityDashboard = lazy(() => import('@/pages/LongevityDashboard'));
const MyHealthReport = lazy(() => import('@/pages/patient-portal/MyHealthReport'));
const LongitudinalSummary = lazy(() => import('@/pages/patient-portal/LongitudinalSummary'));
const MyVaccinations = lazy(() => import('@/pages/patient-portal/MyVaccinations'));

// Pediatric Module
const PediatricDashboard = lazy(() => import('@/pages/PediatricDashboard'));

// Doctor Module
const DoctorHub = lazy(() => import('@/pages/DoctorHub'));
const DoctorAvailability = lazy(() => import('@/pages/DoctorAvailability'));
const DoctorFeeManagement = lazy(() => import('@/pages/DoctorFeeManagement'));
const DoctorBillingDashboard = lazy(() => import('@/pages/DoctorBillingDashboard'));
const DoctorEarnings = lazy(() => import('@/pages/DoctorEarnings'));
const DoctorMessages = lazy(() => import('@/pages/DoctorMessages'));
const RevenueReports = lazy(() => import('@/pages/RevenueReports'));

// Patient Messages
const PatientMessages = lazy(() => import('@/pages/PatientMessages'));

// MedScribe AI
const BillingDashboard = lazy(() => import('@/pages/BillingDashboard'));
const IntakeDashboard = lazy(() => import('@/pages/IntakeDashboard'));
const FollowUpDashboard = lazy(() => import('@/pages/FollowUpDashboard'));
const CdsDashboard = lazy(() => import('@/pages/CdsDashboard'));

// Patient Registration
const NewPatient = lazy(() => import('@/pages/NewPatient'));
const PatientIntakeWizard = lazy(() => import('@/pages/PatientIntakeWizard'));
const PaymentStatus = lazy(() => import('@/pages/PaymentStatus'));

// Encounter-Driven Workflow
const PatientQueue = lazy(() => import('@/pages/PatientQueue'));
const EncounterRedirect = lazy(() => import('@/pages/EncounterRedirect'));
const ClinicalWizard = lazy(() => import('@/pages/ClinicalWizard'));

// Patient CRM
const CrmDashboard = lazy(() => import('@/pages/CrmDashboard'));
const CrmCampaignBuilder = lazy(() => import('@/pages/CrmCampaignBuilder'));
const CrmCampaigns = lazy(() => import('@/pages/CrmCampaigns'));
const CrmSegments = lazy(() => import('@/pages/CrmSegments'));
const CrmTemplates = lazy(() => import('@/pages/CrmTemplates'));
const CrmJourneys = lazy(() => import('@/pages/CrmJourneys'));
const CrmFeedback = lazy(() => import('@/pages/CrmFeedback'));
const CrmLeads = lazy(() => import('@/pages/CrmLeads'));
const CrmInbox = lazy(() => import('@/pages/CrmInbox'));
const CrmEngagement = lazy(() => import('@/pages/CrmEngagement'));
const CrmRevenue = lazy(() => import('@/pages/CrmRevenue'));
const CrmTelephony = lazy(() => import('@/pages/CrmTelephony'));

// User Profile & Settings
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));

import '@/App.css';

// Loading fallback for lazy-loaded pages
const PageLoader = () => (
  <div className="flex items-center justify-center h-[60vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Apply saved theme on initial load (before React renders)
(() => {
  try {
    const saved = JSON.parse(localStorage.getItem('emr_user_settings') || '{}');
    const theme = saved.theme || 'system';
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else if (theme === 'light') {
      root.classList.remove('dark');
    } else {
      root.classList.toggle('dark', window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
  } catch { /* use default */ }
})();

// Root redirect: / → /{role}/dashboard (or /{role}/doctor-hub for doctors)
const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={getRoleBasedRoute(user.role, user.roles)} replace />;
};

// Role-aware dashboard dispatcher
const DashboardRouter = () => {
  const { user } = useAuth();
  if (!user) return null;
  const role = user.role?.toLowerCase();
  if (role === 'superadmin') return <SuperAdminDashboard />;
  if (role === 'tenantadmin') return <TenantAdminDashboard />;
  if (role === 'siteadmin' || role === 'admin') return <AdminDashboard />;
  if (role === 'patient') return <PatientPortal />;
  return <Dashboard />;
};

// Role-aware admin panel dispatcher — tenantadmin sees their own dashboard
const AdminPanelRouter = () => {
  const { user } = useAuth();
  if (!user) return null;
  const role = user.role?.toLowerCase();
  if (role === 'tenantadmin') return <TenantAdminDashboard />;
  return <AdminDashboard />;
};

// Role-aware analytics dispatcher — tenantadmin sees tenant-scoped analytics
const AnalyticsRouter = () => {
  const { user } = useAuth();
  if (!user) return null;
  const role = user.role?.toLowerCase();
  if (role === 'tenantadmin') return <TenantAnalytics />;
  if (['doctor', 'surgeon', 'dentist', 'receptionist', 'staff'].includes(role)) return <AdvancedAnalytics />;
  return <ReportingAnalytics />;
};

// Validates that the URL's :rolePrefix matches the logged-in user's role
const RolePrefixGuard = () => {
  const { user, loading } = useAuth();
  const { rolePrefix } = useParams();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  const expected = getRolePrefix(user.role);
  if (rolePrefix !== expected) {
    return <Navigate to={getRoleBasedRoute(user.role, user.roles)} replace />;
  }
  return <AppLayout />;
};

function App() {
  return (
    <AuthProvider>
      <SiteProvider>
      <div className="App">
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/get-started" element={<GetStarted />} />
            <Route path="/portal/login" element={<PatientLogin />} />
            <Route path="/portal/forgot-password" element={<ForgotPassword />} />

            {/* Root → redirect to role-prefixed dashboard */}
            <Route path="/" element={<RootRedirect />} />

            {/* All protected routes live under /:rolePrefix/ */}
            <Route
              path=":rolePrefix"
              element={
                <ProtectedRoute>
                  <RolePrefixGuard />
                </ProtectedRoute>
              }
            >
              {/* Dashboards — each role lands on their own */}
              <Route path="doctor-hub" element={
                <RoleRoute allowedRoles={['superadmin', 'doctor', 'surgeon', 'dentist', 'physiotherapist']}>
                  <DoctorHub />
                </RoleRoute>
              } />
              <Route path="dashboard" element={<DashboardRouter />} />

              {/* Patient Portal pages */}
              <Route path="patient-portal" element={
                <RoleRoute allowedRoles={['superadmin', 'admin', 'doctor', 'surgeon', 'dentist', 'nurse', 'headnurse', 'receptionist', 'staff', 'patient']}>
                  <PatientPortal />
                </RoleRoute>
              } />
              <Route path="my-visits" element={
                <RoleRoute allowedRoles={['patient']}><PatientVisitsPage /></RoleRoute>
              } />
              <Route path="my-records" element={
                <RoleRoute allowedRoles={['patient']}><MyHealthReport /></RoleRoute>
              } />
              <Route path="my-health-report" element={
                <RoleRoute allowedRoles={['patient']}><MyHealthReport /></RoleRoute>
              } />
              <Route path="my-health-summary" element={
                <RoleRoute allowedRoles={['patient']}><LongitudinalSummary /></RoleRoute>
              } />
              <Route path="my-vaccinations" element={
                <RoleRoute allowedRoles={['patient']}><MyVaccinations /></RoleRoute>
              } />

              {/* Clinical / OPD */}
              <Route path="patients" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'siteadmin', 'admin', 'doctor', 'surgeon', 'dentist', 'physiotherapist', 'nurse', 'headnurse', 'receptionist', 'staff', 'qualityofficer']}>
                  <PatientList />
                </RoleRoute>
              } />
              <Route path="patient-intake" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'siteadmin', 'doctor', 'surgeon', 'dentist', 'nurse', 'headnurse', 'receptionist']}>
                  <PatientIntakeWizard />
                </RoleRoute>
              } />
              <Route path="payment-status" element={<PaymentStatus />} />
              <Route path="patients/new" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'siteadmin', 'doctor', 'surgeon', 'dentist', 'nurse', 'headnurse', 'receptionist', 'staff']}>
                  <NewPatient />
                </RoleRoute>
              } />
              <Route path="patient/:patientId" element={<Patient360 />} />
              <Route path="patient/:patientId/report" element={<Patient360Report />} />
              <Route path="patient/:patientId/pediatric" element={
                <RoleRoute allowedRoles={['superadmin', 'doctor', 'surgeon', 'dentist', 'physiotherapist', 'nurse', 'headnurse']}>
                  <PediatricDashboard />
                </RoleRoute>
              } />
              <Route path="visit/:visitId" element={<VisitScreen />} />
              <Route path="queue" element={
                <RoleRoute allowedRoles={['superadmin', 'doctor', 'surgeon', 'dentist', 'physiotherapist', 'nurse', 'headnurse', 'receptionist', 'staff']}>
                  <PatientQueue />
                </RoleRoute>
              } />
              <Route path="encounter/:visitId" element={<EncounterRedirect />} />
              <Route path="clinical-workflow/:patientId" element={
                <RoleRoute allowedRoles={['superadmin', 'doctor', 'surgeon', 'dentist', 'physiotherapist', 'nurse', 'headnurse', 'receptionist', 'staff']}>
                  <ClinicalWizard />
                </RoleRoute>
              } />

              <Route path="my-availability" element={
                <RoleRoute allowedRoles={['superadmin', 'doctor', 'surgeon', 'dentist', 'physiotherapist']}>
                  <DoctorAvailability />
                </RoleRoute>
              } />

              {/* Diagnostics */}
              <Route path="laboratory" element={
                <ServiceRoute allowedRoles={['superadmin', 'doctor', 'surgeon', 'nurse', 'headnurse', 'labtechnician']} requiredService="Lab">
                  <LaboratorySystem />
                </ServiceRoute>
              } />
              <Route path="radiology" element={
                <RoleRoute allowedRoles={['superadmin', 'doctor', 'surgeon', 'radiologist']}>
                  <RadiologyRIS />
                </RoleRoute>
              } />

              {/* Inventory Management */}
              <Route path="inventory" element={
                <ServiceRoute allowedRoles={['superadmin', 'siteadmin', 'tenantadmin', 'admin', 'nurse', 'headnurse', 'receptionist', 'staff', 'pharmacist', 'labtechnician', 'storekeeper']} requiredService="Inventory">
                  <InventoryManagement />
                </ServiceRoute>
              } />
              <Route path="inventory-analytics" element={
                <ServiceRoute allowedRoles={['superadmin', 'siteadmin', 'tenantadmin', 'admin', 'nurse', 'headnurse', 'receptionist', 'staff', 'pharmacist', 'labtechnician', 'storekeeper']} requiredService="Inventory">
                  <InventoryAnalytics />
                </ServiceRoute>
              } />

              {/* Services */}
              <Route path="pharmacy" element={
                <RoleRoute allowedRoles={['superadmin', 'pharmacist', 'doctor', 'surgeon', 'dentist', 'nurse', 'headnurse']}>
                  <PharmacyManagement />
                </RoleRoute>
              } />
              <Route path="documents" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'siteadmin', 'admin', 'doctor', 'surgeon', 'dentist', 'physiotherapist', 'nurse', 'headnurse', 'receptionist', 'medicalrecords', 'qualityofficer']}>
                  <DocumentManagement />
                </RoleRoute>
              } />

              {/* Doctor Billing */}
              <Route path="doctor-fees" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'siteadmin', 'admin', 'doctor', 'surgeon', 'dentist']}>
                  <DoctorFeeManagement />
                </RoleRoute>
              } />
              <Route path="doctor-billing" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'siteadmin', 'admin', 'doctor', 'surgeon', 'dentist']}>
                  <DoctorBillingDashboard />
                </RoleRoute>
              } />
              <Route path="doctor-earnings" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'siteadmin', 'admin', 'doctor', 'surgeon', 'dentist']}>
                  <DoctorEarnings />
                </RoleRoute>
              } />
              <Route path="revenue-reports" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'siteadmin', 'admin']}>
                  <RevenueReports />
                </RoleRoute>
              } />

              {/* Messaging */}
              <Route path="doctor-messages" element={
                <RoleRoute allowedRoles={['superadmin', 'doctor', 'surgeon', 'dentist', 'physiotherapist']}>
                  <DoctorMessages />
                </RoleRoute>
              } />
              <Route path="my-messages" element={
                <RoleRoute allowedRoles={['patient']}>
                  <PatientMessages />
                </RoleRoute>
              } />

              {/* Billing & RCM */}
              <Route path="billing" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'siteadmin', 'admin', 'receptionist', 'billingstaff']}>
                  <BillingManagement />
                </RoleRoute>
              } />
              <Route path="insurance" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'siteadmin', 'admin', 'receptionist', 'billingstaff']}>
                  <InsuranceManagement />
                </RoleRoute>
              } />
              <Route path="claims" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'siteadmin', 'admin', 'receptionist', 'billingstaff']}>
                  <ClaimsManagement />
                </RoleRoute>
              } />

              {/* IPD & Nursing */}
              <Route path="ipd" element={
                <RoleRoute allowedRoles={['superadmin', 'doctor', 'surgeon', 'anesthetist', 'nurse', 'headnurse']}>
                  <InpatientManagement />
                </RoleRoute>
              } />
              <Route path="bed-management" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'siteadmin', 'admin', 'doctor', 'surgeon', 'nurse', 'headnurse']}>
                  <BedManagement />
                </RoleRoute>
              } />
              <Route path="nursing" element={
                <RoleRoute allowedRoles={['superadmin', 'nurse', 'headnurse', 'doctor', 'surgeon']}>
                  <NursingStation />
                </RoleRoute>
              } />

              {/* OT & Procedures */}
              <Route path="ot" element={
                <RoleRoute allowedRoles={['superadmin', 'doctor', 'surgeon', 'anesthetist']}>
                  <OTManagement />
                </RoleRoute>
              } />
              <Route path="ot/surgery/:surgeryId" element={<SurgeryDetail />} />

              {/* Analytics */}
              <Route path="analytics" element={<AnalyticsRouter />} />
              <Route path="reports" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'siteadmin', 'admin', 'receptionist', 'billingstaff', 'doctor', 'surgeon', 'dentist']}>
                  <Reports />
                </RoleRoute>
              } />

              {/* MedScribe AI */}
              <Route path="medscribe/billing" element={
                <RoleRoute allowedRoles={['superadmin', 'doctor', 'surgeon', 'dentist']}>
                  <BillingDashboard />
                </RoleRoute>
              } />
              <Route path="medscribe/intake" element={
                <RoleRoute allowedRoles={['superadmin', 'doctor', 'surgeon', 'dentist', 'nurse', 'headnurse', 'receptionist']}>
                  <IntakeDashboard />
                </RoleRoute>
              } />
              <Route path="medscribe/followup" element={
                <RoleRoute allowedRoles={['superadmin', 'doctor', 'surgeon', 'dentist', 'nurse', 'headnurse']}>
                  <FollowUpDashboard />
                </RoleRoute>
              } />
              <Route path="medscribe/cds" element={
                <RoleRoute allowedRoles={['superadmin', 'doctor', 'surgeon', 'dentist']}>
                  <CdsDashboard />
                </RoleRoute>
              } />

              {/* Health Intelligence */}
              <Route path="wearables" element={
                <RoleRoute allowedRoles={['superadmin', 'doctor', 'surgeon', 'nurse', 'headnurse']}>
                  <WearableIntegration />
                </RoleRoute>
              } />
              <Route path="remote-monitoring" element={
                <RoleRoute allowedRoles={['superadmin', 'doctor', 'surgeon', 'nurse', 'headnurse']}>
                  <RemoteMonitoring />
                </RoleRoute>
              } />

              <Route path="patient/:patientId/longevity" element={
                <RoleRoute allowedRoles={['superadmin', 'doctor', 'surgeon']}>
                  <LongevityDashboard />
                </RoleRoute>
              } />

              {/* Patient CRM */}
              <Route path="crm" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin']}>
                  <CrmDashboard />
                </RoleRoute>
              } />
              <Route path="crm/campaigns" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin']}>
                  <CrmCampaigns />
                </RoleRoute>
              } />
              <Route path="crm/campaigns/new" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin']}>
                  <CrmCampaignBuilder />
                </RoleRoute>
              } />
              <Route path="crm/campaigns/:id" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin']}>
                  <CrmCampaigns />
                </RoleRoute>
              } />
              <Route path="crm/segments" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin']}>
                  <CrmSegments />
                </RoleRoute>
              } />
              <Route path="crm/templates" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin']}>
                  <CrmTemplates />
                </RoleRoute>
              } />
              <Route path="crm/journeys" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin']}>
                  <CrmJourneys />
                </RoleRoute>
              } />
              <Route path="crm/feedback" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin']}>
                  <CrmFeedback />
                </RoleRoute>
              } />
              <Route path="crm/leads" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin']}>
                  <CrmLeads />
                </RoleRoute>
              } />
              <Route path="crm/inbox" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin']}>
                  <CrmInbox />
                </RoleRoute>
              } />
              <Route path="crm/engagement" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin']}>
                  <CrmEngagement />
                </RoleRoute>
              } />
              <Route path="crm/revenue" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin']}>
                  <CrmRevenue />
                </RoleRoute>
              } />
              <Route path="crm/telephony" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'receptionist', 'billingstaff', 'staff', 'siteadmin']}>
                  <CrmTelephony />
                </RoleRoute>
              } />

              {/* User Profile & Settings */}
              <Route path="profile" element={<ProfilePage />} />
              <Route path="settings" element={<SettingsPage />} />

              {/* Admin */}
              <Route path="admin-panel" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'siteadmin', 'admin', 'hrmanager', 'itadmin']}>
                  <AdminPanelRouter />
                </RoleRoute>
              } />
              <Route path="config" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'siteadmin', 'admin', 'itadmin']}>
                  <AdminConfiguration />
                </RoleRoute>
              } />
              <Route path="platform-settings" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'siteadmin', 'admin', 'itadmin']}>
                  <PlatformSettings />
                </RoleRoute>
              } />
              <Route path="department-templates" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin', 'admin']}>
                  <DepartmentTemplatesAdmin />
                </RoleRoute>
              } />

              {/* Super Admin */}
              <Route path="tenants" element={
                <RoleRoute allowedRoles={['superadmin']}><SuperAdminDashboard /></RoleRoute>
              } />
              <Route path="tenants/:tenantId" element={
                <RoleRoute allowedRoles={['superadmin']}><TenantDetail /></RoleRoute>
              } />
              <Route path="platform-master-data" element={
                <RoleRoute allowedRoles={['superadmin']}><PlatformMasterData /></RoleRoute>
              } />
              <Route path="subscription-management" element={
                <RoleRoute allowedRoles={['superadmin']}><SubscriptionManagement /></RoleRoute>
              } />
              <Route path="billing-consolidation" element={
                <RoleRoute allowedRoles={['superadmin']}><BillingConsolidation /></RoleRoute>
              } />

              {/* Tenant Admin */}
              <Route path="sites" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin']}><SiteManagement /></RoleRoute>
              } />
              <Route path="users" element={
                <RoleRoute allowedRoles={['superadmin', 'tenantadmin']}><UserManagement /></RoleRoute>
              } />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </div>
      </SiteProvider>
    </AuthProvider>
  );
}

export default App;
